<template>
    <el-timeline-item class="project-timeline__item rel"
                      :timestamp="label"
                      :hide-timestamp="!label"
                      :placement="placement">
        <template #dot>
            <div class="wh-1.5 f-center rounded-half bg-gray-400">
                <icon v-if="icon"
                      :name="icon"
                      class="fill-white stroke-none wh-0.9" />

                <i v-if="elIcon"
                   :class="elIcon"
                   class="text-white" />
            </div>
        </template>

        <!-- Show All -->
        <el-button v-if="type === projectTimelineTypes.DOCUMENTS || canShowAll"
                   class="abs t-0 r-1"
                   type="text"
                   @click="$emit('show-all')">
            Смотреть все
        </el-button>


        <!-- Label -->
        <div v-if="!label" 
             class="_pt-0.5 _pb-1 fs-1">
            {{ timelineLabel.prefix }}
            <span class="font-bold">{{ timelineLabel.text }}</span>
            <span v-if="isApprovalProcess"
                  class="font-bold"> 
                {{ type === projectTimelineTypes.APPROVAL_PROCESS ? 'в раздел ' : 'в разделе ' }}
                "{{ value.translated_type }}"
            </span>
        </div>

        <!-- Tasks -->
        <div v-if="isTasks"
             class="space-y-0.5">
            <div v-for="task in displayedValue"
                 :key="task.id"
                 class="f-col space-y-0.15">
                <div class="w-fit f-y-center space-x-0.5 cursor-pointer hover:text-accent hover:stroke-accent"
                     @click="goTo(task)">
                    <span>
                        {{ task.translated_type }} №{{ task.number_in_project }}
                    </span>
                    <icon name="link"
                          class="wh-1" />
                </div>
                <div class="f-y-center space-x-0.5 text-gray-500 fs-0.75">
                    <span>{{ task.name }}</span>

                    <template v-if="withMembers && task.members">
                        <div v-for="member in task.members"
                             :key="member.id"
                             class="f-y-center space-x-0.5">
                            <i class="el-icon-caret-right fs-0.5" />
                            <span>{{ member.translated_sub_role || member.translated_role }}: {{ getName(member.user) }}</span>
                        </div>
                    </template>

                    <template v-if="!withMembers && withInitiator && task.initiator">
                        <i class="el-icon-caret-right fs-0.5" />
                        <span>{{ task.initiator.translated_role }}: {{ getName(task.initiator.user) }}</span>
                    </template>
                </div>

                <template v-if="withAddress">
                    <div v-if="getRelatedObjects(task, { withLabels: true })?.labels?.position"
                         class="text-gray-500 fs-0.75">
                        {{ getRelatedObjects(task, { withLabels: true }).labels.position }}
                    </div>
                </template>
            </div>
        </div>

        <!-- Approval process -->
        <div v-if="isApprovalProcess"
             class="space-y-0.5">
            <div v-for="task in displayedValue"
                 :key="task.id"
                 class="f-y-start space-x-0.5">
                <!-- Document card -->
                <document-thumb class="p-0 w-fit"
                                :document="task.data?.main_document"
                                with-icon
                                can-download>
                    <template #header>
                        <el-tag type="info"
                                size="mini"
                                class="w-fit">
                            Основной документ
                        </el-tag>
                    </template>
                </document-thumb>
                
                <el-button type="text"
                           size="mini"
                           class="p-0 _mt-0.3 hover:stroke-accent"
                           @click="goTo(task)">
                    <icon name="link"
                          class="wh-1" />
                </el-button>
            </div>
        </div>

        <!-- Photos -->
        <div v-if="isPhotos"
             class="cols-3 rows-min gap-0.5">
            <photo-card v-for="photo in displayedValue"
                        :key="photo.id"
                        :photo="photo" />
        </div>

        <!-- For custom value -->
        <slot />

        <el-button v-if="type !== projectTimelineTypes.DOCUMENTS && canShowAll"
                   type="text"
                   size="mini"
                   @click="$emit('show-all')">
            {{ timelineLabel.more }}
        </el-button>
    </el-timeline-item>
</template>

<script>
import { getTimelineLabel, projectTimelineTypes } from '@/models/projectTimeline'
import { getName } from '@/models/users'
import { getRelatedObjects, types } from '@/models/tasks'

import PhotoCard from '@/components/photos/PhotoCard'
import DocumentThumb from '@/components/documents/DocumentThumb'

export default {
  name: 'ProjectTimelineItem',

  components: {
    PhotoCard,
    DocumentThumb
  },

  props: {
    placement: { type: String, default: 'top' },
    icon: { type: String, default: '' },
    elIcon: { type: String, default: '' },
    
    type: { type: String, default: '' },
    value: { type: [Array, Object], default: () => [] },
    label: { type: String, default: '' },

    displayedValueCount: { type: Number, default: 3 },

    withMembers: { type: Boolean, default: false },
    withInitiator: { type: Boolean, default: false },
    withAddress: { type: Boolean, default: false }
  },

  data() {
    return {
      projectTimelineTypes,
      getName,
      getRelatedObjects
    }
  },

  computed: {
    isTasks() {
      return [
        projectTimelineTypes.ACCEPTANCE_OF_WORK,
        projectTimelineTypes.DEFECTS_AND_VIOLATIONS,
        projectTimelineTypes.DEFECTS_AND_VIOLATIONS_FINISHED
      ].includes(this.type)
    },

    isApprovalProcess() {
      return [
        projectTimelineTypes.APPROVAL_PROCESS,
        projectTimelineTypes.APPROVAL_PROCESS_APPROVED
      ].includes(this.type)
    },

    isPhotos() {
      return this.type === projectTimelineTypes.PHOTOS
    },

    timelineLabel() {
      return getTimelineLabel((this.value?.value || this.value).length, this.displayedValueCount, this.type) 
    },

    displayedValue() {
      return this.displayedValueCount ? (this.value?.value || this.value).slice(0, this.displayedValueCount) : (this.value?.value || this.value)
    },

    canShowAll() {
      return this.displayedValueCount ? (this.value?.value || this.value).length > this.displayedValueCount : false
    }
  },

  methods: {
    goTo(task) {
      (({
        [types.APPROVAL_PROCESS]: () => this.$router.push({
          name: 'project.approval.info',
          params: { processId: task.id }
        })
      })[task.type] || (() => this.$router.push({
        name: 'project.task',
        params: {
          taskId: task.id
        }
      })))()
    }
  }
}
</script>

<style lang="scss">
.project-timeline__item.el-timeline-item {
  &:last-child {
    padding-bottom: 0;
  }

  .el-timeline-item {
    &__tail {
      left: 11px;
    }
    
    &__wrapper {
      padding-left: 2rem;
    }
  
    &__timestamp.is-top  {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      font-size: 1rem;
      color: #313131;
    }
  }
}
</style>